import { last, noop } from "lodash";
import { TEMPLATE_KEYS } from "PFApp/constants/templates";
import React, { createContext, useCallback, useContext, useState } from "react";

import { Bar, Row } from "../../types";
import { useDisplayOrder, UseDisplayOrderType } from "./use_display_order";

const REMEMBERED_DETAILS_COUNT = 2;

const DEFAULT_DISPLAY_ORDER_VALUE: UseDisplayOrderType = {
  pushToDisplayOrderStack: () => {},
  popFromDisplayOrderStack: () => {},
  clearDisplayOrderStack: () => {},
  checkTopValue: () => true
};

export const activityTypeToSidePanelTypeMap = {
  [TEMPLATE_KEYS.ROLE]: "role",
  [TEMPLATE_KEYS.AUDIT_ROLE]: "role",
  [TEMPLATE_KEYS.ENGAGEMENT]: "engagement",
  [TEMPLATE_KEYS.AUDIT_ENGAGEMENT]: "engagement"
} as const;

export type DetailsPanelType = "booking" | "role" | "engagement" | "profile" | "overbookings";

export type DetailsPanelData = {
  id: number;
  type: DetailsPanelType;
  openAllocateManually?: boolean;
  isEditPermitted?: boolean;
  roleBookingsIds?: number[];
  bars?: Bar[][];
} & Partial<Pick<Row, "original">>;

type DetailsPanelContext = {
  detailsData?: DetailsPanelData;
  openDetailsPanel: (data: DetailsPanelData, stack?: boolean) => void;
  closeDetailsPanel: () => void;
  closeAllDetailsPanels: () => void;
  displayOrder: UseDisplayOrderType;
  detailsDataStack: DetailsPanelData[];
};

const DetailsPanelContext = createContext({
  openDetailsPanel: noop,
  closeDetailsPanel: noop,
  displayOrder: DEFAULT_DISPLAY_ORDER_VALUE
} as DetailsPanelContext);

export const DetailsPanelContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [detailsData, setDetailsData] = useState<DetailsPanelData>();
  const [detailsDataStack, setDetailsDataStack] = useState<DetailsPanelData[]>([]);
  const displayOrder = useDisplayOrder();
  const { pushToDisplayOrderStack, popFromDisplayOrderStack, clearDisplayOrderStack } = displayOrder;

  const setDetailsPanelData = useCallback(
    (data: DetailsPanelData, stack = false) => {
      if (stack) {
        setDetailsDataStack((previousStack) => {
          if (previousStack.length === 0 && detailsData) {
            return [detailsData, data];
          }
          const lastNDetailsData = previousStack.slice(-REMEMBERED_DETAILS_COUNT);
          return [...lastNDetailsData, data];
        });
        pushToDisplayOrderStack(data.type);
      } else if (!stack) {
        setDetailsDataStack([]);
        clearDisplayOrderStack();
      }
      setDetailsData(data);
    },
    [detailsData, detailsDataStack]
  );

  const popDetailsPanelData = useCallback(() => {
    const stack = [...detailsDataStack.slice(0, detailsDataStack.length - 1)];
    const detailsPanelDataToSet = last(stack);

    setDetailsDataStack(stack);
    setDetailsData(detailsPanelDataToSet);
    popFromDisplayOrderStack();
  }, [detailsDataStack]);

  const closeAllDetailsPanels = useCallback(() => {
    setDetailsDataStack([]);
    setDetailsData(undefined);
    clearDisplayOrderStack();
  }, []);

  const value = {
    detailsData,
    openDetailsPanel: setDetailsPanelData,
    closeDetailsPanel: popDetailsPanelData,
    closeAllDetailsPanels,
    displayOrder,
    detailsDataStack
  };

  return <DetailsPanelContext.Provider value={value}>{children}</DetailsPanelContext.Provider>;
};

export const useDetailsPanelContext = () => useContext(DetailsPanelContext);
